import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useCallback } from "react";

interface Ad {
  id: string;
  advertiser_name: string;
  advertiser_email: string | null;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  image_url: string;
  link_url: string;
  ad_type: 'banner' | 'sidebar';
  pricing_model: 'cpm' | 'cpc';
  price_per_unit: number;
  budget: number;
  spent: number;
  impressions: number;
  clicks: number;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  is_approved: boolean;
  target_categories: string[];
  created_at: string;
  updated_at: string;
}

// Fetch active ads by type
export const useActiveAds = (adType: 'banner' | 'sidebar') => {
  return useQuery({
    queryKey: ['active-ads', adType],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .eq('ad_type', adType)
        .eq('is_active', true)
        .eq('is_approved', true)
        .or(`start_date.is.null,start_date.lte.${now}`)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Ad[];
    },
  });
};

// Fetch all ads for admin
export const useAllAds = () => {
  return useQuery({
    queryKey: ['all-ads'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Ad[];
    },
  });
};

// Record impression
export const useRecordImpression = () => {
  return useMutation({
    mutationFn: async (adId: string) => {
      // Record in ad_impressions table
      await supabase.from('ad_impressions').insert({
        ad_id: adId,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        ip_hash: 'anonymous', // We don't track real IPs for privacy
      });

      // Increment impressions count on ad
      const { data: ad } = await supabase
        .from('ads')
        .select('impressions, price_per_unit, pricing_model, spent')
        .eq('id', adId)
        .single();

      if (ad) {
        const newImpressions = (ad.impressions || 0) + 1;
        const additionalSpent = ad.pricing_model === 'cpm' ? (ad.price_per_unit / 1000) : 0;
        
        await supabase
          .from('ads')
          .update({ 
            impressions: newImpressions,
            spent: (ad.spent || 0) + additionalSpent
          })
          .eq('id', adId);
      }
    },
  });
};

// Record click
export const useRecordClick = () => {
  return useMutation({
    mutationFn: async (adId: string) => {
      // Record in ad_clicks table
      await supabase.from('ad_clicks').insert({
        ad_id: adId,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        ip_hash: 'anonymous',
      });

      // Increment clicks count on ad
      const { data: ad } = await supabase
        .from('ads')
        .select('clicks, price_per_unit, pricing_model, spent')
        .eq('id', adId)
        .single();

      if (ad) {
        const newClicks = (ad.clicks || 0) + 1;
        const additionalSpent = ad.pricing_model === 'cpc' ? ad.price_per_unit : 0;
        
        await supabase
          .from('ads')
          .update({ 
            clicks: newClicks,
            spent: (ad.spent || 0) + additionalSpent
          })
          .eq('id', adId);
      }
    },
  });
};

// Create ad
export const useCreateAd = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (ad: Omit<Ad, 'id' | 'created_at' | 'updated_at' | 'impressions' | 'clicks' | 'spent'>) => {
      const { data, error } = await supabase
        .from('ads')
        .insert(ad)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-ads'] });
    },
  });
};

// Update ad
export const useUpdateAd = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Ad> & { id: string }) => {
      const { data, error } = await supabase
        .from('ads')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-ads'] });
      queryClient.invalidateQueries({ queryKey: ['active-ads'] });
    },
  });
};

// Delete ad
export const useDeleteAd = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ads')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-ads'] });
    },
  });
};

// Custom hook to track ad impression when component mounts
export const useAdImpression = (adId: string | undefined) => {
  const recordImpression = useRecordImpression();
  
  useEffect(() => {
    if (adId) {
      recordImpression.mutate(adId);
    }
  }, [adId]);
};

// Custom hook to handle ad click
export const useAdClick = () => {
  const recordClick = useRecordClick();
  
  const handleClick = useCallback((adId: string, linkUrl: string) => {
    recordClick.mutate(adId);
    window.open(linkUrl, '_blank', 'noopener,noreferrer');
  }, [recordClick]);
  
  return handleClick;
};
